import * as helpers from '@curbnturf/helpers';
import { toPrecision } from '@curbnturf/helpers';
import {
  ActivityLocation,
  FlowStepType,
  FlowType,
  IActivityLocationSearchRequest,
  IBoondock,
  IBoondockPoint,
  ILatLon,
  IListing,
  IListingPoint,
  ISignUpFlowStep,
  ISite,
  ISubPhoto,
  IUser,
} from '@curbnturf/objects';
import {
  DEFAULT_IMAGE_4X3_URL,
  DEFAULT_IMAGE_FLAT_URL,
  DEFAULT_IMAGE_MOTORHOME_URL,
  DEFAULT_IMAGE_TITLE_URL,
  DEFAULT_IMAGE_URL,
  DEFAULT_MAP_LATITUDE,
  DEFAULT_MAP_LONGITUDE,
  GUEST_FLOW_RV,
  GUEST_FLOW_TENT,
  HOST_BOONDOCK_FLOW,
  HOST_MULTISITE_FLOW,
  HOST_SINGLESITE_FLOW,
  IMAGE_URL,
  SALES_MULTISITE_FLOW,
  SALES_SINGLESITE_FLOW,
} from './constants';
import { ICancellableDebounceObject, IPoiFilterPanelOptions } from './interfaces';

const urlCleanRegex = /[\W_]+/g;

export namespace HelperFunctions {
  export const getPhotoUrl = (photo: ISubPhoto) => {
    if (photo && photo.key) {
      return helpers.getPhotoUrl(IMAGE_URL, photo);
    }
    return DEFAULT_IMAGE_URL;
  };

  export const getPhotoUrl3x4Large = (photo: ISubPhoto) => {
    if (photo && photo.key) {
      return helpers.getPhotoUrl3x4Large(IMAGE_URL, photo);
    }
    return DEFAULT_IMAGE_4X3_URL;
  };

  export const getPhotoUrl3x4Medium = (photo: ISubPhoto) => {
    if (photo && photo.key) {
      return helpers.getPhotoUrl3x4Medium(IMAGE_URL, photo);
    } else {
      return DEFAULT_IMAGE_4X3_URL;
    }
  };

  export const getPhotoUrl3x4Small = (photo: ISubPhoto) => {
    if (photo && photo.key) {
      return helpers.getPhotoUrl3x4Small(IMAGE_URL, photo);
    }
    return DEFAULT_IMAGE_4X3_URL;
  };

  export const getPhotoUrl3x4Thumbnail = (photo: ISubPhoto) => {
    if (photo && photo.key) {
      return helpers.getPhotoUrl3x4Thumbnail(IMAGE_URL, photo);
    }
    return DEFAULT_IMAGE_4X3_URL;
  };

  export const getPhotoUrlMedium = (photo: ISubPhoto) => {
    if (photo && photo.key) {
      return helpers.getPhotoUrlMedium(IMAGE_URL, photo);
    }
    return DEFAULT_IMAGE_URL;
  };

  export const getPhotoUrlFlatMedium = (photo: ISubPhoto) => {
    if (photo && photo.key) {
      return helpers.getPhotoUrlFlatMedium(IMAGE_URL, photo);
    }
    return DEFAULT_IMAGE_FLAT_URL;
  };

  export const getPhotoUrlFlatSmall = (photo: ISubPhoto) => {
    if (photo && photo.key) {
      return helpers.getPhotoUrlFlatSmall(IMAGE_URL, photo);
    }
    return DEFAULT_IMAGE_FLAT_URL;
  };

  export const getPhotoUrlFlatThumbnail = (photo: ISubPhoto) => {
    if (photo && photo.key) {
      return helpers.getPhotoUrlFlatThumbnail(IMAGE_URL, photo);
    }
    return DEFAULT_IMAGE_FLAT_URL;
  };

  export const getPhotoUrlSquareMedium = (photo: ISubPhoto) => {
    if (photo && photo.key) {
      return helpers.getPhotoUrlSquareMedium(IMAGE_URL, photo);
    }
    return DEFAULT_IMAGE_MOTORHOME_URL;
  };

  export const getPhotoUrlSquareSmall = (photo: ISubPhoto) => {
    if (photo && photo.key) {
      return helpers.getPhotoUrlSquareSmall(IMAGE_URL, photo);
    }
    return DEFAULT_IMAGE_URL;
  };

  export const getPhotoUrlSquareBlogThumbnail = (photo: ISubPhoto) => {
    if (photo && photo.key) {
      return helpers.getPhotoUrlSquareBlogThumbnail(IMAGE_URL, photo);
    }
    return DEFAULT_IMAGE_URL;
  };

  export const getPhotoUrlSquareThumbnail = (photo: ISubPhoto) => {
    if (photo && photo.key) {
      return helpers.getPhotoUrlSquareThumbnail(IMAGE_URL, photo);
    }
    return DEFAULT_IMAGE_URL;
  };

  export const getPhotoUrlSquareTiny = (photo: ISubPhoto) => {
    if (photo && photo.key) {
      return helpers.getPhotoUrlSquareTiny(IMAGE_URL, photo);
    }
    return DEFAULT_IMAGE_URL;
  };

  export const getPhotoUrlTitleCropped = (photo: ISubPhoto) => {
    if (photo && photo.key) {
      return helpers.getPhotoUrlTitleCropped(IMAGE_URL, photo);
    }
    return DEFAULT_IMAGE_TITLE_URL;
  };

  export const getPhotoUrlFullSize = (photo: ISubPhoto) => {
    if (photo && photo.key) {
      return helpers.getPhotoUrlFullSize(IMAGE_URL, photo);
    }
    return DEFAULT_IMAGE_URL;
  };

  export const getPhotoUrlOriginal = (photo: ISubPhoto) => {
    if (photo && photo.key) {
      return helpers.getPhotoUrlOriginal(IMAGE_URL, photo);
    }
    return DEFAULT_IMAGE_URL;
  };

  export const getDefaultLocation = (): ILatLon => {
    return { lat: DEFAULT_MAP_LATITUDE, lon: DEFAULT_MAP_LONGITUDE };
  };

  export const locationIsDefault = (location: ILatLon): boolean => {
    return location.lat === getDefaultLocation().lat && location.lon === getDefaultLocation().lon;
  };

  export const buildListingPointRoute = (point: IListingPoint): string[] => {
    const route: string[] = [''];

    route.push('rv-camping');

    if ('location' in point && point.location) {
      route.push(encodeURIComponent(point.location?.replace(urlCleanRegex, '_')));
    }
    if ('address' in point && point.address) {
      if (
        point.address.city &&
        point.address.city.trim().length > 0 &&
        point.address.state &&
        point.address.state.trim().length
      ) {
        route.push(
          encodeURIComponent(
            point.address.city?.replace(urlCleanRegex, '-') + '_' + point.address.state?.replace(urlCleanRegex, '-'),
          ),
        );
      }
    }
    if (point.name) {
      route.push(encodeURIComponent(point.name?.replace(urlCleanRegex, '-')));
    }
    if (point.id) {
      route.push(point.id.toString());
    }
    if (point.syncId && !point.id) {
      route.push(point.syncId as string);
    }
    return route;
  };

  export const buildBoondockingPointRoute = (point: IBoondockPoint): string[] => {
    const route: string[] = [''];

    route.push('boondocking');

    if ('location' in point && point.location) {
      route.push(encodeURIComponent(point.location?.replace(urlCleanRegex, '_')));
    }
    if ('address' in point && point.address) {
      if (
        point.address.city &&
        point.address.city.trim().length > 0 &&
        point.address.state &&
        point.address.state.trim().length
      ) {
        route.push(
          encodeURIComponent(
            point.address.city?.replace(urlCleanRegex, '-') + '_' + point.address.state?.replace(urlCleanRegex, '-'),
          ),
        );
      }
    }
    if (point.name) {
      route.push(encodeURIComponent(point.name?.replace(urlCleanRegex, '-')));
    }
    if (point.id) {
      route.push(point.id.toString());
    }
    if (point.syncId && !point.id) {
      route.push(point.syncId as string);
    }
    return route;
  };

  export const buildListingRoute = (listing: IListing): string[] => {
    const route: string[] = [''];
    route.push('rv-camping');
    if ('address' in listing && listing.address) {
      if (
        listing.address.city &&
        listing.address.city.trim().length > 0 &&
        listing.address.state &&
        listing.address.state.trim().length > 0
      ) {
        route.push(
          encodeURIComponent(
            listing.address.city?.replace(urlCleanRegex, '-') +
              '_' +
              listing.address.state?.replace(urlCleanRegex, '-'),
          ),
        );
      }
    }
    if (listing.name) {
      route.push(encodeURIComponent(listing.name?.replace(urlCleanRegex, '-')));
    }
    if (listing.id) {
      route.push(listing.id.toString());
    }
    if ((listing as IListingPoint).syncId && !listing.id) {
      route.push((listing as IListingPoint).syncId as string);
    }
    return route;
  };

  export const buildBoondockRoute = (boondock: IBoondock): string[] => {
    const route: string[] = [''];
    route.push('boondocking');
    if (boondock?.address) {
      if (
        boondock.address.city &&
        boondock.address.city.trim().length > 0 &&
        boondock.address.state &&
        boondock.address.state.trim().length > 0
      ) {
        route.push(
          encodeURIComponent(
            boondock.address.city?.replace(urlCleanRegex, '-') +
              '_' +
              boondock.address.state?.replace(urlCleanRegex, '-'),
          ),
        );
      }
    }
    if (boondock.name) {
      route.push(encodeURIComponent(boondock.name?.replace(urlCleanRegex, '-')));
    }
    if (boondock.id) {
      route.push(boondock.id.toString());
    }
    if ((boondock as IBoondockPoint).syncId && !boondock.id) {
      route.push((boondock as IBoondockPoint).syncId as string);
    }
    return route;
  };

  export const buildActivityRoute = (poi: ActivityLocation): string[] => {
    const route: string[] = [''];

    if (!poi) {
      return route;
    }

    if (poi.categoryDump) {
      route.push('rv-dump-site');
    } else if (poi.categoryRestArea) {
      route.push('rest-area');
    } else if (poi.categoryWifi) {
      route.push('wifi');
    } else if (poi.categoryPotableWater) {
      route.push('fresh-water');
    } else if (poi.categoryPropane) {
      route.push('propane');
    } else if (poi.activityBiking) {
      route.push('biking');
    } else if (poi.activityBoating) {
      route.push('boating');
    } else if (poi.activityFishing) {
      route.push('fishing');
    } else if (poi.activityGolf) {
      route.push('golf');
    } else if (poi.activityHiking) {
      route.push('hiking');
    } else if (poi.activityHorseback) {
      route.push('horseback-riding');
    } else if (poi.activityHunting) {
      route.push('hunting');
    } else if (poi.activityKayaking) {
      route.push('kayaking');
    } else if (poi.activityMotorSports) {
      route.push('motor-sports');
    } else if (poi.activityOhv) {
      route.push('ohv-trails');
    } else if (poi.activityRafting) {
      route.push('rafting');
    } else if (poi.activityRockClimbing) {
      route.push('rock-climbing');
    } else if (poi.activitySnowSports) {
      route.push('snow-sports');
    } else if (poi.activitySurfing) {
      route.push('surfing');
    } else if (poi.activitySwimming) {
      route.push('swimming');
    } else if (poi.activityWaterSports) {
      route.push('water-sports');
    } else if (poi.activityWildlifeWatching) {
      route.push('wildlife-watching');
    } else if (poi.activityWindSports) {
      route.push('wind-sports');
    } else {
      route.push('activity');
    }

    if (poi.address?.city && poi.address?.state) {
      route.push(
        encodeURIComponent(
          poi.address.city.replace(urlCleanRegex, '-') + '_' + poi.address.state.replace(urlCleanRegex, '-'),
        ),
      );
    }

    route.push(encodeURIComponent(poi.getName().replace(urlCleanRegex, '-')));

    if (poi.id) {
      route.push(poi.id.toString());
    }

    return route;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  export const debounce = <F extends (...args: any[]) => any>(func: F, waitFor: number) => {
    let timeout: ReturnType<typeof setTimeout>;

    return (...args: Parameters<F>): Promise<ReturnType<F>> =>
      new Promise((resolve) => {
        if (timeout) {
          clearTimeout(timeout);
        }

        timeout = setTimeout(() => resolve(func(...args)), waitFor);
      });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  export const debounceCancellable = <F extends (...args: any[]) => any>(func: F, waitFor: number) => {
    const obj: ICancellableDebounceObject = {};

    return (...args: Parameters<F>): ICancellableDebounceObject => {
      if (obj.timeout) {
        clearTimeout(obj.timeout);
      }

      obj.timeout = setTimeout(() => func(...args), waitFor);

      return obj;
    };
  };

  export function poiSearchRequestToPoiFilterOptions(
    searchRequest: IActivityLocationSearchRequest,
  ): IPoiFilterPanelOptions {
    const custom =
      searchRequest.activities
        ?.filter((activity) => {
          return (
            activity !== 'biking' &&
            activity !== 'boating' &&
            activity !== 'fishing' &&
            activity !== 'golf' &&
            activity !== 'hiking' &&
            activity !== 'horseback' &&
            activity !== 'hunting' &&
            activity !== 'kayaking' &&
            activity !== 'motorSports' &&
            activity !== 'ohv' &&
            activity !== 'rafting' &&
            activity !== 'rockClimbing' &&
            activity !== 'snowSports' &&
            activity !== 'surfing' &&
            activity !== 'swimming' &&
            activity !== 'waterSports' &&
            activity !== 'wildlifeWatching' &&
            activity !== 'windSports'
          );
        })
        .join(',') || '';

    return {
      activity: searchRequest.selected?.activity || false,
      activities: {
        biking: searchRequest.activities?.includes('biking') || false,
        boating: searchRequest.activities?.includes('boating') || false,
        fishing: searchRequest.activities?.includes('fishing') || false,
        golf: searchRequest.activities?.includes('golf') || false,
        hiking: searchRequest.activities?.includes('hiking') || false,
        horseback: searchRequest.activities?.includes('horseback') || false,
        hunting: searchRequest.activities?.includes('hunting') || false,
        kayaking: searchRequest.activities?.includes('kayaking') || false,
        motorSports: searchRequest.activities?.includes('motorSports') || false,
        ohv: searchRequest.activities?.includes('ohv') || false,
        rafting: searchRequest.activities?.includes('rafting') || false,
        rockClimbing: searchRequest.activities?.includes('rockClimbing') || false,
        snowSports: searchRequest.activities?.includes('snowSports') || false,
        surfing: searchRequest.activities?.includes('surfing') || false,
        swimming: searchRequest.activities?.includes('swimming') || false,
        waterSports: searchRequest.activities?.includes('waterSports') || false,
        wildlifeWatching: searchRequest.activities?.includes('wildlifeWatching') || false,
        windSports: searchRequest.activities?.includes('windSports') || false,
        custom,
      },
      dump: searchRequest.selected?.dump || false,
      restArea: searchRequest.selected?.restArea || false,
      wifi: searchRequest.selected?.wifi || false,
      potableWater: searchRequest.selected?.potableWater || false,
      propane: searchRequest.selected?.propane || false,
    };
  }

  export function boondockCompletePercentage(flow: FlowType, boondock: IBoondock, user?: IUser) {
    if (!user && boondock.user) {
      user = boondock.user;
    }

    const latestStep = findBoondockLatestStep(flow, boondock, user);
    const steps = findSteps(flow, undefined, user);

    const currentStep = steps.find((el) => el.type === latestStep);
    if (currentStep) {
      const foundIndex = steps.findIndex((el) => el.type === currentStep.type);
      return toPrecision((foundIndex / (steps.length - 1)) * 100, 0);
    }

    return 0;
  }

  /**
   *
   * @param flow
   * @param listing
   * @param user
   * @param {ISite | undefined} selectedSite
   */
  export function listingCompletePercentage(flow: FlowType, listing: IListing, user?: IUser, selectedSite?: ISite) {
    if (!user && listing.user) {
      user = listing.user;
    }

    const latestStep = findListingLatestStep(flow, listing, user, selectedSite);
    const steps = findSteps(flow, listing, user);

    const currentStep = steps.find((el) => el.type === latestStep);
    if (currentStep) {
      const foundIndex = steps.findIndex((el) => el.type === currentStep.type);
      return toPrecision((foundIndex / (steps.length - 1)) * 100, 0);
    }

    return 0;
  }

  export function siteCompletePercentage(selectedSite: ISite) {
    // @todo iterate the steps and check for completeness

    const steps: ISignUpFlowStep[] = [
      { type: FlowStepType.SiteDetails, title: 'Site Details', route: ['', 'sign-up', 'details'], complete: false },
      {
        type: FlowStepType.SiteAmenities,
        title: 'Site Amenities',
        route: ['', 'sign-up', 'amenities'],
        complete: false,
      },
      {
        type: FlowStepType.SiteActivities,
        title: 'Site Activities',
        route: ['', 'sign-up', 'activities'],
        complete: false,
      },
      {
        type: FlowStepType.SiteIsolation,
        title: 'Site Conditions/Isolation',
        route: ['', 'sign-up', 'isolation'],
        complete: false,
      },
      { type: FlowStepType.SiteRVs, title: 'Vehicles Allowed On Site', route: ['', 'sign-up', 'rvs'], complete: false },
      { type: FlowStepType.SitePrice, title: 'Site Pricing', route: ['', 'sign-up', 'price'], complete: false },
      {
        type: FlowStepType.SiteDescription,
        title: 'Site Name and Description',
        route: ['', 'sign-up', 'site-name'],
        complete: false,
      },
      { type: FlowStepType.SitePhotos, title: 'Site Photos', route: ['', 'sign-up', 'site-photos'], complete: false },
    ];

    steps.reduceRight((prevStep, currentStep) => {
      switch (currentStep.type) {
        case FlowStepType.SiteAmenities:
          if ((selectedSite?.amenities && selectedSite.amenities.length > 0) || prevStep?.complete) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.SiteActivities:
          if ((selectedSite?.activities && selectedSite.activities.length > 0) || prevStep?.complete) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.SiteIsolation:
          if (
            selectedSite?.noise?.level &&
            (selectedSite?.privacy?.home ||
              selectedSite?.privacy?.secluded ||
              selectedSite?.privacy?.others ||
              selectedSite?.privacy?.secluded ||
              selectedSite?.privacy?.neighbor)
          ) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.SiteRVs:
          if (
            selectedSite?.vehicles?.classA ||
            selectedSite?.vehicles?.classA ||
            selectedSite?.vehicles?.classB ||
            selectedSite?.vehicles?.classC ||
            selectedSite?.vehicles?.fifthWheel ||
            selectedSite?.vehicles?.travelTrailer ||
            selectedSite?.vehicles?.truckCamper ||
            selectedSite?.vehicles?.teardrop ||
            selectedSite?.vehicles?.popup ||
            prevStep?.complete
          ) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.SiteDetails:
          if (selectedSite?.amenities) {
            const foundParking = selectedSite.amenities.find((el) => el.type === 'pullThrough' || el.type === 'backIn');
            if (selectedSite?.maxGuests && selectedSite?.maxGuests >= 1 && foundParking) {
              currentStep.complete = true;
            }
          }
          break;
        case FlowStepType.SitePrice:
          if (selectedSite && (selectedSite.price || selectedSite.price === 0)) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.SiteDescription:
          if (selectedSite?.name && selectedSite?.name?.length >= 3) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.SitePhotos:
          if (selectedSite?.photos && selectedSite?.photos?.length >= 1) {
            currentStep.complete = true;
            break;
          }
      }
      return currentStep;
    });

    let firstIncompleteStep = undefined;
    for (let x in steps) {
      if (!steps[x].complete) {
        firstIncompleteStep = steps[x];
        break;
      }
    }

    const latestStep = firstIncompleteStep?.type;

    const currentStep = steps.find((el) => el.type === latestStep);
    if (currentStep) {
      const foundIndex = steps.findIndex((el) => el.type === currentStep.type);
      return toPrecision((foundIndex / (steps.length - 1)) * 100, 0);
    }

    return 0;
  }

  export function findSteps(flow: FlowType, listing?: IListing, user?: IUser): ISignUpFlowStep[] {
    let steps: ISignUpFlowStep[];

    if (flow === FlowType.listing) {
      if (listing?.sites && Array.isArray(listing.sites)) {
        if (listing.sites.length > 1) {
          steps = HOST_MULTISITE_FLOW.slice();
        } else {
          steps = HOST_SINGLESITE_FLOW.slice();
        }
      } else {
        steps = HOST_SINGLESITE_FLOW.slice();
      }
    } else if (flow === FlowType.boondock) {
      steps = HOST_BOONDOCK_FLOW.slice();
    } else if (flow === FlowType.sales) {
      if (listing?.sites && Array.isArray(listing.sites)) {
        if (listing.sites.length > 1) {
          steps = SALES_MULTISITE_FLOW.slice();
        } else {
          steps = SALES_SINGLESITE_FLOW.slice();
        }
      } else {
        steps = SALES_SINGLESITE_FLOW.slice();
      }
    } else {
      if (user?.selectedRv) {
        if (user?.selectedRv.tent) {
          steps = GUEST_FLOW_TENT.slice();
        } else {
          steps = GUEST_FLOW_RV.slice();
        }
      } else {
        steps = GUEST_FLOW_RV.slice();
      }
    }

    // Make the steps editable.
    return steps.map((step) => ({ ...step }));
  }

  export function findListingLatestStep(
    flow: FlowType,
    listing?: IListing,
    user?: IUser,
    selectedSite?: ISite,
  ): FlowStepType | undefined {
    const steps = findSteps(flow, listing, user);

    // Will be relying on the array being ordered
    // Check each step's completeness
    steps.reduceRight((prevStep, currentStep) => {
      switch (currentStep.type) {
        case FlowStepType.RoleSelector:
          if (user?.role) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.Source:
          if (user?.source) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.ProfilePhone:
          if (user?.photo && user?.phone) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.ListingTerrain:
          if (listing?.landTypes && listing?.landTypes.length > 0) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.SiteAmenities:
          if ((selectedSite?.amenities && selectedSite.amenities.length > 0) || prevStep?.complete) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.SiteActivities:
          if ((selectedSite?.activities && selectedSite.activities.length > 0) || prevStep?.complete) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.SiteIsolation:
          if (
            selectedSite?.noise?.level &&
            (selectedSite?.privacy?.home ||
              selectedSite?.privacy?.secluded ||
              selectedSite?.privacy?.others ||
              selectedSite?.privacy?.secluded ||
              selectedSite?.privacy?.neighbor)
          ) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.SiteRVs:
          if (
            selectedSite?.vehicles?.classA ||
            selectedSite?.vehicles?.classA ||
            selectedSite?.vehicles?.classB ||
            selectedSite?.vehicles?.classC ||
            selectedSite?.vehicles?.fifthWheel ||
            selectedSite?.vehicles?.travelTrailer ||
            selectedSite?.vehicles?.truckCamper ||
            selectedSite?.vehicles?.teardrop ||
            selectedSite?.vehicles?.popup ||
            prevStep?.complete
          ) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.SiteDetails:
          if (selectedSite?.amenities) {
            const foundParking = selectedSite.amenities.find((el) => el.type === 'pullThrough' || el.type === 'backIn');
            if (selectedSite?.maxGuests && selectedSite?.maxGuests >= 1 && foundParking) {
              currentStep.complete = true;
            }
          }
          break;
        case FlowStepType.SitePrice:
          if (
            selectedSite &&
            (selectedSite.price || selectedSite.price === 0) &&
            listing?.cancellationPolicy !== undefined
          ) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.ListingMultiSite:
          if (listing?.sites && listing?.sites.length > 0) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.ListingSiteManagement:
          if (listing?.sites && listing.sites.length > 0) {
            const completedSites = listing.sites.filter((site) => site.available);
            if (completedSites.length > 0) {
              currentStep.complete = true;
            }
          }
          break;
        case FlowStepType.ListingBooking:
          if (
            listing?.checkIn &&
            listing.checkOut &&
            listing.minLengthOfStayInDays &&
            listing.maxLengthOfStayInDays &&
            (listing.reserveDaysInAdvance || listing.reserveDaysInAdvance === 0) &&
            listing.bookingWindow &&
            listing.checkInProcess &&
            listing.reservationProcess
          ) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.ListingRules:
          if ((listing?.policies && listing.policies.length >= 1) || prevStep?.complete) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.ListingLocation:
          if (
            ((listing?.address &&
              listing?.address.address &&
              listing.address.city &&
              listing.address.state &&
              listing.address.zipcode) ||
              (listing?.coordinates && listing.coordinates.lat && listing.coordinates.lon)) &&
            listing?.landOwner &&
            listing?.landOwner?.self !== undefined &&
            listing?.landOwner?.self !== null
          ) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.SiteDescription:
          if (selectedSite?.name && selectedSite?.name?.length >= 3) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.ListingDescription:
          if (
            listing?.name &&
            listing?.name?.length >= 5 &&
            listing?.description &&
            listing?.description?.length >= 150
          ) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.UserProfilePhoto:
          if (user?.photo && user?.photo?.key?.length > 1) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.SitePhotos:
          if (selectedSite?.photos && selectedSite?.photos?.length >= 1) {
            currentStep.complete = true;
          } else if (selectedSite?.available === true) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.ListingPhotos:
          if (selectedSite?.photos && selectedSite?.photos?.length >= 3) {
            currentStep.complete = true;
          } else if (listing?.publishDate !== undefined) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.UserGarage:
          if (user?.selectedRv && ((user?.selectedRv?.name && user?.selectedRv?.type) || user?.selectedRv?.tent)) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.UserRv:
          if (user?.selectedRv && user?.selectedRv?.name && user?.selectedRv?.type) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.UserRvSize:
          if (
            user?.selectedRv &&
            (user?.selectedRv.year ||
              user?.selectedRv.make ||
              user?.selectedRv.model ||
              user?.selectedRv.height ||
              user?.selectedRv.width ||
              user?.selectedRv.length ||
              user?.selectedRv.underwayLength ||
              user?.selectedRv.underwayHeight ||
              user?.selectedRv.underwayWidth ||
              user?.selectedRv.clearance ||
              user?.selectedRv.weight ||
              user?.selectedRv.axleCount)
          ) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.UserRoadConditions:
          if (
            user?.selectedRv &&
            (user?.selectedRv.roadCondition?.offRoad ||
              user?.selectedRv.roadCondition?.paved ||
              user?.selectedRv.roadCondition?.smoothGravel ||
              user?.selectedRv.roadCondition?.poorGravel ||
              user?.selectedRv.roadCondition?.highClearance)
          ) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.UserTent:
          if (
            user?.selectedRv &&
            (user?.selectedRv.tent ||
              user?.selectedRv.tentWidth ||
              user?.selectedRv.tentHeight ||
              user?.selectedRv.tentLength ||
              user?.selectedRv.tentCount)
          ) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.SalesContact:
          if ((user?.firstName?.length || 0) >= 2 && (user?.lastName?.length || 0) >= 2) {
            currentStep.complete = true;
          }
          break;
      }

      return currentStep;
    });

    let firstIncompleteStep = undefined;
    for (let x in steps) {
      if (!steps[x].complete) {
        firstIncompleteStep = steps[x];
        break;
      }
    }

    return firstIncompleteStep?.type;
  }

  export function findBoondockSteps(): ISignUpFlowStep[] {
    const steps: ISignUpFlowStep[] = HOST_BOONDOCK_FLOW.slice();

    // Make the steps editable.
    return steps.map((step) => ({ ...step }));
  }

  export function findBoondockLatestStep(flow: FlowType, boondock?: IBoondock, user?: IUser): FlowStepType | undefined {
    const steps = findSteps(flow, undefined, user);

    // Will be relying on the array being ordered
    // Check each step's completeness
    steps.reduceRight((prevStep, currentStep) => {
      switch (currentStep.type) {
        case FlowStepType.RoleSelector:
          if (user?.role) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.Source:
          if (user?.source) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.ProfilePhone:
          if (user?.photo && user?.phone) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.ListingTerrain:
          if (boondock?.landTypes && boondock?.landTypes.length > 0) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.SiteAmenities:
          if ((boondock?.amenities && boondock.amenities.length > 0) || prevStep?.complete) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.SiteActivities:
          if ((boondock?.activities && boondock.activities.length > 0) || prevStep?.complete) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.SiteIsolation:
          if (boondock?.noise?.level) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.SiteBoondockLand:
          if ((boondock?.sizeCars && boondock?.sizeCars > 0) || (boondock?.sizeBuses && boondock?.sizeBuses > 0)) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.SiteDetails:
          if (boondock?.amenities) {
            const foundParking = boondock.amenities.find((el) => el.type === 'pullThrough' || el.type === 'backIn');
            if (foundParking) {
              currentStep.complete = true;
            }
          }
          break;
        case FlowStepType.ListingRules:
          if ((boondock?.policies && boondock.policies.length >= 1) || prevStep?.complete) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.SiteLocation:
          if (
            ((boondock?.address &&
              boondock?.address.address &&
              boondock.address.city &&
              boondock.address.state &&
              boondock.address.zipcode) ||
              (boondock?.coordinates && boondock.coordinates.lat && boondock.coordinates.lon)) &&
            boondock?.landOwner &&
            boondock?.landOwner?.self !== undefined &&
            boondock?.landOwner?.self !== null
          ) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.SiteDescription:
          if (
            boondock?.name &&
            boondock?.name?.length >= 5 &&
            boondock?.description &&
            boondock?.description?.length >= 150
          ) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.UserProfilePhoto:
          if (user?.photo && user?.photo?.key?.length > 1) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.SitePhotos:
          if (boondock?.photos && boondock?.photos?.length >= 3) {
            currentStep.complete = true;
          } else if (boondock?.publishDate !== undefined) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.UserGarage:
          if (user?.selectedRv && ((user?.selectedRv?.name && user?.selectedRv?.type) || user?.selectedRv?.tent)) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.UserRv:
          if (user?.selectedRv && user?.selectedRv?.name && user?.selectedRv?.type) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.UserRvSize:
          if (
            user?.selectedRv &&
            (user?.selectedRv.year ||
              user?.selectedRv.make ||
              user?.selectedRv.model ||
              user?.selectedRv.height ||
              user?.selectedRv.width ||
              user?.selectedRv.length ||
              user?.selectedRv.underwayLength ||
              user?.selectedRv.underwayHeight ||
              user?.selectedRv.underwayWidth ||
              user?.selectedRv.clearance ||
              user?.selectedRv.weight ||
              user?.selectedRv.axleCount)
          ) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.UserRoadConditions:
          if (
            user?.selectedRv &&
            (user?.selectedRv.roadCondition?.offRoad ||
              user?.selectedRv.roadCondition?.paved ||
              user?.selectedRv.roadCondition?.smoothGravel ||
              user?.selectedRv.roadCondition?.poorGravel ||
              user?.selectedRv.roadCondition?.highClearance)
          ) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.UserTent:
          if (
            user?.selectedRv &&
            (user?.selectedRv.tent ||
              user?.selectedRv.tentWidth ||
              user?.selectedRv.tentHeight ||
              user?.selectedRv.tentLength ||
              user?.selectedRv.tentCount)
          ) {
            currentStep.complete = true;
          }
          break;
        case FlowStepType.SalesContact:
          if ((user?.firstName?.length || 0) > 2 && (user?.lastName?.length || 0) > 2) {
            currentStep.complete = true;
          }
          break;
      }

      return currentStep;
    });

    let firstIncompleteStep = undefined;
    for (let x in steps) {
      if (!steps[x].complete) {
        firstIncompleteStep = steps[x];
        break;
      }
    }

    return firstIncompleteStep?.type;
  }
  // @todo perhaps this could be it's own helper file, this file is getting big.
  export function isListingStepComplete(step: FlowStepType, listing: IListing, user?: IUser) {
    switch (step) {
      case FlowStepType.RoleSelector:
        if (user?.role) {
          return true;
        }
        break;
      case FlowStepType.Source:
        if (user?.source) {
          return true;
        }
        break;
      case FlowStepType.ProfilePhone:
        if (user?.photo && user?.phone) {
          return true;
        }
        break;
      case FlowStepType.ListingTerrain:
        if (listing?.landTypes && listing?.landTypes.length > 0) {
          return true;
        }
        break;
      case FlowStepType.ListingLocation:
        if (
          ((listing?.address &&
            listing?.address.address &&
            listing.address.city &&
            listing.address.state &&
            listing.address.zipcode) ||
            (listing?.coordinates && listing.coordinates.lat && listing.coordinates.lon)) &&
          listing?.landOwner &&
          listing?.landOwner?.self !== undefined &&
          listing?.landOwner?.self !== null
        ) {
          return true;
        }
        break;
      case FlowStepType.ListingDescription:
        if (
          listing?.name &&
          listing?.name?.length >= 5 &&
          listing?.description &&
          listing?.description?.length >= 150
        ) {
          return true;
        }
        break;
      case FlowStepType.ListingMultiSite:
        if (listing?.sites && listing?.sites.length > 0) {
          return true;
        }
        break;
      // @todo refactor these sections.
      // case FlowStepType.SiteAmenities:
      //   if (listing?.amenities && listing.amenities.length > 0) {
      //     return true;
      //   }
      //   break;
      // case FlowStepType.SiteActivities:
      //   if (listing?.activities && listing.activities.length > 0) {
      //     return true;
      //   }
      //   break;
      // case FlowStepType.SiteIsolation:
      //   if (
      //     listing?.type === 'standard' &&
      //     listing?.noise?.level &&
      //     (listing.privacy?.home ||
      //       listing?.privacy?.secluded ||
      //       listing?.privacy?.others ||
      //       listing?.privacy?.secluded ||
      //       listing?.privacy?.neighbor)
      //   ) {
      //     return true;
      //   }
      //   if (listing?.type === 'boondock' && listing?.noise?.level) {
      //     return true;
      //   }
      //   break;
      // case FlowStepType.SiteBoondockLand:
      //   if ((listing?.sizeCars && listing?.sizeCars > 0) || (listing?.sizeBuses && listing?.sizeBuses > 0)) {
      //     return true;
      //   }
      //   break;
      // case FlowStepType.SiteRVs:
      //   if (
      //     listing?.vehicles?.classA ||
      //     listing?.vehicles?.classA ||
      //     listing?.vehicles?.classB ||
      //     listing?.vehicles?.classC ||
      //     listing?.vehicles?.fifthWheel ||
      //     listing?.vehicles?.travelTrailer ||
      //     listing?.vehicles?.truckCamper ||
      //     listing?.vehicles?.teardrop ||
      //     listing?.vehicles?.popup
      //   ) {
      //     return true;
      //   }
      //   break;
      // case FlowStepType.SiteDetails:
      //   if (listing?.amenities) {
      //     const foundParking = listing.amenities.find((el) => el.type === 'pullThrough' || el.type === 'backIn');
      //     if (listing?.maxGuests && listing?.maxGuests >= 1 && foundParking) {
      //       return true;
      //     }
      //   }
      //   break;
      // case FlowStepType.SitePrice:
      //   if (listing && (listing.price || listing.price === 0) && listing?.cancellationPolicy !== undefined) {
      //     return true;
      //   }
      //   break;
      case FlowStepType.ListingBooking:
        if (
          listing?.checkIn &&
          listing.checkOut &&
          listing.minLengthOfStayInDays &&
          listing.maxLengthOfStayInDays &&
          (listing.reserveDaysInAdvance || listing.reserveDaysInAdvance === 0) &&
          listing.bookingWindow &&
          listing.checkInProcess &&
          listing.reservationProcess
        ) {
          return true;
        }
        break;
      case FlowStepType.ListingRules:
        if (listing?.policies && listing.policies.length >= 1) {
          return true;
        }
        break;
      case FlowStepType.SiteLocation:
        if (listing.sites && listing.sites.length > 0) {
          for (let i = 0; i < listing?.sites?.length; i++) {
            if (
              !(listing?.sites[i].coordinates && listing.sites[i].coordinates?.lat && listing.sites[i].coordinates?.lon)
            ) {
              return false;
            }
          }
          return true;
        }
        break;
      case FlowStepType.SiteDescription:
        if (
          listing?.name &&
          listing?.name?.length >= 5 &&
          listing?.description &&
          listing?.description?.length >= 150
        ) {
          return true;
        }
        break;
      case FlowStepType.UserProfilePhoto:
        if (user?.photo && user?.photo?.key?.length > 1) {
          return true;
        }
        break;
      case FlowStepType.UserGarage:
        if (user?.selectedRv && ((user?.selectedRv?.name && user?.selectedRv?.type) || user?.selectedRv?.tent)) {
          return true;
        }
        break;
      case FlowStepType.UserRv:
        if (user?.selectedRv && user?.selectedRv?.name && user?.selectedRv?.type) {
          return true;
        }
        break;
      case FlowStepType.UserRvSize:
        if (
          user?.selectedRv &&
          (user?.selectedRv.year ||
            user?.selectedRv.make ||
            user?.selectedRv.model ||
            user?.selectedRv.height ||
            user?.selectedRv.width ||
            user?.selectedRv.length ||
            user?.selectedRv.underwayLength ||
            user?.selectedRv.underwayHeight ||
            user?.selectedRv.underwayWidth ||
            user?.selectedRv.clearance ||
            user?.selectedRv.weight ||
            user?.selectedRv.axleCount)
        ) {
          return true;
        }
        break;
      case FlowStepType.UserRoadConditions:
        if (
          user?.selectedRv &&
          (user?.selectedRv.roadCondition?.offRoad ||
            user?.selectedRv.roadCondition?.paved ||
            user?.selectedRv.roadCondition?.smoothGravel ||
            user?.selectedRv.roadCondition?.poorGravel ||
            user?.selectedRv.roadCondition?.highClearance)
        ) {
          return true;
        }
        break;
      case FlowStepType.UserTent:
        if (
          user?.selectedRv &&
          (user?.selectedRv.tent ||
            user?.selectedRv.tentWidth ||
            user?.selectedRv.tentHeight ||
            user?.selectedRv.tentLength ||
            user?.selectedRv.tentCount)
        ) {
          return true;
        }
        break;
      case FlowStepType.ListingPhotos:
        if (listing?.photos && listing?.photos?.length >= 3) {
          return true;
        } else if (listing?.publishDate !== undefined) {
          return true;
        }
        break;
      case FlowStepType.ListingPublish:
        if (listing?.publishDate !== undefined) {
          return true;
        }
        break;
    }

    return false;
  }

  export function isSiteStepComplete(step: FlowStepType, site: ISite, user?: IUser) {
    switch (step) {
      case FlowStepType.RoleSelector:
        if (user?.role) {
          return true;
        }
        break;
      case FlowStepType.Source:
        if (user?.source) {
          return true;
        }
        break;
      case FlowStepType.ProfilePhone:
        if (user?.photo && user?.phone) {
          return true;
        }
        break;
      case FlowStepType.SiteAmenities:
        if (site?.amenities && site.amenities.length > 0) {
          return true;
        }
        break;
      case FlowStepType.SiteActivities:
        if (site?.activities && site.activities.length > 0) {
          return true;
        }
        break;
      case FlowStepType.SiteIsolation:
        if (
          site?.noise?.level &&
          (site.privacy?.home ||
            site?.privacy?.secluded ||
            site?.privacy?.others ||
            site?.privacy?.secluded ||
            site?.privacy?.neighbor)
        ) {
          return true;
        }
        break;
      case FlowStepType.SiteRVs:
        if (
          site?.vehicles?.classA ||
          site?.vehicles?.classA ||
          site?.vehicles?.classB ||
          site?.vehicles?.classC ||
          site?.vehicles?.fifthWheel ||
          site?.vehicles?.travelTrailer ||
          site?.vehicles?.truckCamper ||
          site?.vehicles?.teardrop ||
          site?.vehicles?.popup
        ) {
          return true;
        }
        break;
      case FlowStepType.SiteDetails:
        if (site?.amenities) {
          const foundParking = site.amenities.find((el) => el.type === 'pullThrough' || el.type === 'backIn');
          if (site?.maxGuests && site?.maxGuests >= 1 && foundParking) {
            return true;
          }
        }
        break;
      case FlowStepType.SitePrice:
        if (site && (site.price || site.price === 0)) {
          return true;
        }
        break;
      case FlowStepType.SiteLocation:
        if (site.coordinates && site.coordinates?.lat && site.coordinates?.lon) {
          return true;
        }
        break;
      case FlowStepType.SiteDescription:
        if (site?.name && site?.name?.length >= 5 && site?.description && site?.description?.length >= 150) {
          return true;
        }
        break;
      case FlowStepType.SitePhotos:
        if (site?.photos && site?.photos?.length >= 3) {
          return true;
        } else if (site?.publishDate !== undefined) {
          return true;
        }
        break;
    }

    return false;
  }
}
